import { useState, type PointerEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { motion, useMotionValue, useReducedMotion, useSpring } from 'framer-motion'
import { ArrowUpRight } from 'lucide-react'
import './project-showcase.css'

interface Project {
  title: string
  description: string
  category: string
  period?: string
  link: string
  preview: ReactNode
}

export function ProjectShowcase({ projects }: { projects: Project[] }) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const reducedMotion = useReducedMotion()
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const smoothX = useSpring(x, { stiffness: 250, damping: 30 })
  const smoothY = useSpring(y, { stiffness: 250, damping: 30 })

  const movePreview = (event: PointerEvent<HTMLAnchorElement>, index: number) => {
    if (!window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)').matches) return
    const nextX = Math.max(16, Math.min(event.clientX + 24, window.innerWidth - 336))
    const nextY = Math.max(16, Math.min(event.clientY - 100, window.innerHeight - 226))
    // Start at the pointer rather than flying in from the previous row or page corner.
    if (hoveredIndex === null) {
      smoothX.jump(nextX)
      smoothY.jump(nextY)
    }
    x.set(nextX)
    y.set(nextY)
    setHoveredIndex(index)
  }

  return (
    <div className="project-showcase" onPointerLeave={() => setHoveredIndex(null)}>
      {projects.map((project, index) => (
        <Link
          key={project.link}
          to={project.link}
          className="project-showcase-row group relative block border-t border-vibe-border py-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-primary"
          onPointerEnter={(event) => movePreview(event, index)}
          onPointerMove={(event) => movePreview(event, index)}
          onPointerLeave={() => setHoveredIndex(null)}
          onFocus={() => setHoveredIndex(null)}
          onClick={() => setHoveredIndex(null)}
        >
          <div className="project-showcase-highlight pointer-events-none absolute inset-0 rounded-lg bg-vibe-elevated opacity-0 transition-opacity duration-200 motion-reduce:transition-none group-focus-visible:opacity-100" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0 max-w-3xl">
              <div className="mb-2 font-mono text-xs uppercase tracking-wider text-accent-primary">{project.category}</div>
              <h3 className="flex items-baseline gap-2 text-xl font-medium tracking-tight text-text-heading lg:text-2xl">
                <span className="project-showcase-title underline-offset-4 group-focus-visible:underline">{project.title}</span>
                <ArrowUpRight aria-hidden="true" className="project-showcase-arrow h-4 w-4 shrink-0 text-accent-primary transition-transform duration-200 motion-reduce:transition-none" />
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-text-secondary lg:text-base">{project.description}</p>
            </div>
            {project.period && <span className="shrink-0 pt-1 font-mono text-xs tabular-nums text-text-secondary">{project.period}</span>}
          </div>
          <div className="project-showcase-inline relative mt-5 max-w-sm overflow-hidden rounded-lg border border-vibe-border bg-vibe-elevated" aria-hidden="true">
            <div className="project-showcase-preview aspect-[16/10]">{project.preview}</div>
          </div>
        </Link>
      ))}
      <div className="border-t border-vibe-border" />

      {createPortal(
        <motion.div
          aria-hidden="true"
          className="project-showcase-floating pointer-events-none fixed left-0 top-0 z-50 h-[210px] w-80 overflow-hidden rounded-xl border border-vibe-border bg-vibe-elevated shadow-2xl"
          style={{ x: reducedMotion ? x : smoothX, y: reducedMotion ? y : smoothY }}
          animate={{ opacity: hoveredIndex === null ? 0 : 1, scale: hoveredIndex === null ? 0.96 : 1 }}
          transition={{ duration: reducedMotion ? 0 : 0.18 }}
        >
          {projects.map((project, index) => (
            <div key={project.link} className="project-showcase-preview absolute inset-0 transition-opacity duration-200 motion-reduce:transition-none" style={{ opacity: hoveredIndex === index ? 1 : 0 }}>
              {project.preview}
            </div>
          ))}
        </motion.div>,
        document.body,
      )}
    </div>
  )
}
